import React, { useCallback, useState } from 'react';
import * as moment from 'moment';
import * as R from 'ramda';
import { Route } from 'react-router';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { useIsAvailableInPlanPackage } from '@common/hooks/use-is-available-in-plan-package';
import { EPermissions, ELevels, EPlanPackageConfig } from '@common/definitions';
import Overview from '@common/components/overview';
import Table from '@common/components/table';
import Permission from '@common/components/permission';
import Dropdown from '@common/components/dropdown';
import Icon from '@common/components/icon';
import { Button } from '@common/components/button';
import LinkWrapper from '@common/components/linkWrapper';
import RoleUsersModel from '@modules/admin/components/role-users-model';
import RoleForm from '../../forms/role';
import { TrackComponent, pageWrapper, EEventNames } from '../../../../../client/analytics';
import * as userSelector from '../../../core/selectors/logged-user';
import * as organisationSelector from '../../../organisation/selectors/organisation';

import './styles.scss';
import ProfilePopover from '../../../../common/components/profile/popover';

const RoleComponent = ({ item, onOpenRole }) => [
  <a onClick={() => onOpenRole(item)}>
    {item.name}
    {item.is_global && <Icon type="star__filled" className="GlobalRole__Icon" />}
  </a>,
  item.is_global || !item.last_updated_by ? (
    <span style={{ opacity: 0.5 }}>Oneteam</span>
  ) : (
    <>
      <LinkWrapper url={`/admin/users/${item.last_updated_by.id}/about`} className="tw-text-inherit hover:tw-text-inherit tw-block hover:tw-underline">
        <ProfilePopover user={item.last_updated_by} key={`userId-${item.last_updated_by.id}`} className="tw-text-inherit hover:tw-text-inherit">
          <span>{item.last_updated_by.full_name}</span>
        </ProfilePopover>
      </LinkWrapper>
      <small>{moment(item.created_at).format('D MMMM YYYY')}</small>
    </>
  ),
  <RoleUsersModel role={item}>
    <div>
      <Trans i18nKey="organisation:role_users_count" values={{ count: item.member_count || 0 }} />
    </div>
  </RoleUsersModel>,
];

const mapDetailPropsToProps = (state) => ({
  organisation: organisationSelector.selected(state),
  roles: organisationSelector.roles(state),
});

const mapDetailDispatchToProps = {
  fetchPermissionGroups: require('../../../organisation/actions').fetchPermissionGroups,
};

const DetailRoute = connect(mapDetailPropsToProps, mapDetailDispatchToProps)(({
  history, match: { params }, roles, organisation, fetchPermissionGroups,
}) => {
  const role = R.find(
    R.propEq('id', params.roleId),
    parseInt(params.level, 10) === ELevels.NETWORK ? roles.network : roles.organisation,
  );

  if (!role) return null;

  return (
    <TrackComponent eventName={EEventNames.VISITED_ORGANISATION_ROLE_DETAIL_PAGE} properties={{ level: params.level }}>
      <RoleForm
        form="edit-role"
        role={role}
        organisation={organisation}
        show
        onFetchGroups={fetchPermissionGroups}
        level={parseInt(params.level, 10)}
        onExited={() => history.push('/admin/admins/roles')}
      />
    </TrackComponent>
  );
});

const OrganisationSettingsRolesContainer = ({
  history, organisation, roles, fetchRoles, fetchPermissionGroups, removeRole, t,
}) => {
  const [showOrgRoleForm, setShowOrgRoleForm] = useState(false);
  const [showNetworkRoleForm, setShowNetworkRoleForm] = useState(false);

  const {
    isAvailable: canCreateRole, showUpgradeModal,
  } = useIsAvailableInPlanPackage(EPlanPackageConfig.ADVANCED_USER_ROLES);

  const onShowOrgRoleForm = useCallback(() => {
    if (canCreateRole) setShowOrgRoleForm(true);
    else showUpgradeModal();
  }, [canCreateRole, showUpgradeModal, setShowOrgRoleForm]);

  const onShowNetworkRoleForm = useCallback(() => {
    if (canCreateRole) setShowNetworkRoleForm(true);
    else showUpgradeModal();
  }, [canCreateRole, showUpgradeModal, setShowNetworkRoleForm]);

  const columns = [
    { label: t('organisation:settings_roles_name'), className: 'Table__Cell__Title', size: '45%' },
    { label: t('organisation:settings_roles_last_modified_by'), size: '25%' },
    { label: t('organisation:settings_roles_users'), className: 'Table__Cell--clickable', size: '100px' },
  ];

  const onOpenRole = (item) => history.push(`/admin/admins/roles/${item.level}/${item.id}`);

  return (
    <>
      <Route
        path="/admin/admins/roles/:level/:roleId"
        component={DetailRoute}
      />
      <Overview>
        <Overview.Header>
          <Overview.Header.Title learnMoreHref={t('organisation:settings_organisation_roles_learn_more_link')}>
            <Trans i18nKey="organisation:settings_organisation_roles" />
          </Overview.Header.Title>
          <Permission name={EPermissions.ORGANISATION_ROLES_CREATE}>
            <div className="pull-right">
              <RoleForm
                form="create-organisation-role"
                organisation={organisation}
                onFetchGroups={fetchPermissionGroups}
                level={ELevels.ORGANISATION}
                onShow={onShowOrgRoleForm}
                show={showOrgRoleForm}
                onExited={useCallback(() => setShowOrgRoleForm(false), [setShowOrgRoleForm])}
              >
                <Button type="primary" iconRight="add" size="large">
                  <Trans i18nKey="organisation:settings_create_role" />
                </Button>
              </RoleForm>
            </div>
          </Permission>
        </Overview.Header>
        <Overview.Content>
          <Table
            columns={columns}
            items={roles.organisation}
            data={{ onFetch: fetchRoles }}
            renderRow={RoleComponent}
            rowProps={{ onOpenRole }}
            placeholder={(
              <div className="Table__Placeholder">
                <Trans i18nKey="organisation:settings_admins_empty" />
              </div>
            )}
            ActionComponent={({ item }) => (item.is_global ? (
              <Button onClick={() => onOpenRole(item)}><Trans i18nKey="organisation:settings_roles_view_role" /></Button>
            ) : (
              <Dropdown alignRight toggle={<Button><Trans i18nKey="organisation:settings_roles_actions" /></Button>}>
                <Permission name={EPermissions.ORGANISATION_ROLES_UPDATE}>
                  <Dropdown.Item onClick={() => onOpenRole(item)}>
                    <Trans i18nKey="organisation:settings_roles_actions_edit" />
                  </Dropdown.Item>
                </Permission>
                <Permission name={EPermissions.ORGANISATION_ROLES_REMOVE}>
                  <Dropdown.Item onClick={() => removeRole(item.id)} danger>
                    <Trans i18nKey="organisation:settings_roles_actions_remove" />
                  </Dropdown.Item>
                </Permission>
              </Dropdown>
            ))}
          />
        </Overview.Content>
      </Overview>
      <Overview>
        <Overview.Header>
          <Overview.Header.Title learnMoreHref={t('organisation:settings_location_roles_learn_more_link')}>
            <Trans i18nKey="organisation:settings_location_roles" />
          </Overview.Header.Title>
          <Permission name={EPermissions.ORGANISATION_ROLES_CREATE}>
            <RoleForm
              form="create-network-role"
              organisation={organisation}
              onFetchGroups={fetchPermissionGroups}
              level={ELevels.NETWORK}
              onShow={onShowNetworkRoleForm}
              show={showNetworkRoleForm}
              onExited={useCallback(() => setShowNetworkRoleForm(false), [setShowNetworkRoleForm])}
            >
              <Button type="primary" iconRight="add" size="large">
                <Trans i18nKey="organisation:settings_create_role" />
              </Button>
            </RoleForm>
          </Permission>
        </Overview.Header>
        <Overview.Content>
          <Table
            columns={columns}
            items={roles.network}
            data={{
              onFetch: fetchRoles,
            }}
            renderRow={RoleComponent}
            rowProps={{
              onOpenRole,
            }}
            placeholder={(
              <div className="Table__Placeholder">
                <Trans i18nKey="organisation:settings_roles_empty" />
              </div>
            )}
            ActionComponent={({ item }) => (item.is_global ? (
              <Button onClick={() => onOpenRole(item)}><Trans i18nKey="organisation:settings_roles_view_role" /></Button>
            ) : (
              <Dropdown alignRight toggle={<Button><Trans i18nKey="organisation:settings_roles_actions" /></Button>}>
                <Permission name={EPermissions.ORGANISATION_ROLES_UPDATE}>
                  <Dropdown.Item onClick={() => onOpenRole(item)}>
                    <Trans i18nKey="organisation:settings_roles_actions_edit" />
                  </Dropdown.Item>
                </Permission>
                <Permission name={EPermissions.ORGANISATION_ROLES_REMOVE}>
                  <Dropdown.Item onClick={() => removeRole(item.id)} danger>
                    <Trans i18nKey="organisation:settings_roles_actions_remove" />
                  </Dropdown.Item>
                </Permission>
              </Dropdown>
            ))}
          />
        </Overview.Content>
      </Overview>
    </>
  );
};

const mapStateToProps = (state) => ({
  loggedUser: userSelector.selected(state),
  organisation: organisationSelector.selected(state),
  roles: organisationSelector.roles(state),
});

const mapDispatchToProps = {
  fetchRoles: require('../../../organisation/actions').fetchRoles,
  fetchPermissionGroups: require('../../../organisation/actions').fetchPermissionGroups,
  removeRole: require('../../actions').removeRole,
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(
  pageWrapper(EEventNames.VISITED_ORGANISATION_ROLES_PAGE)(OrganisationSettingsRolesContainer)
));
